import { useEffect, useState } from "react";
import axios from "axios";
import {
  Package, Users, Clock, RefreshCw, CheckCircle2, AlertTriangle, MinusCircle,
} from "lucide-react";
import { API } from "../../config";

/**
 * BuildAdoptionWidget
 *
 * Tracks how quickly users pick up a new frontend bundle after a deploy.
 * Powered by /api/build-adoption/ping (called by BuildVersionWatcher on
 * every page load) and /api/admin/build-adoption/stats.
 *
 * Why this matters: if the BuildVersionWatcher (post 11/06/2026 hotfix)
 * is doing its job, we should see ≥80% adoption_1h on every deploy. If
 * it drops, the watcher's nudge isn't aggressive enough and we should
 * consider a forced reload after N hours.
 */

const _badgeForRate = (rate, total) => {
  if (!total || total < 3) return { theme: "neutral", label: "Campione insufficiente" };
  if (rate >= 0.80) return { theme: "good",     label: "Adoption ottima (≥80%)" };
  if (rate >= 0.50) return { theme: "warning",  label: "Adoption parziale (50–79%)" };
  return                     { theme: "critical", label: "Adoption bassa (<50%)" };
};

const THEMES = {
  good:     { text: "text-green-300",  border: "border-green-500/30",  bg: "bg-green-500/5",   Icon: CheckCircle2 },
  warning:  { text: "text-amber-300",  border: "border-amber-500/30",  bg: "bg-amber-500/5",   Icon: AlertTriangle },
  critical: { text: "text-red-300",    border: "border-red-500/30",    bg: "bg-red-500/5",     Icon: AlertTriangle },
  neutral:  { text: "text-gray-400",   border: "border-gray-700",      bg: "bg-black/30",      Icon: MinusCircle },
};

const _shortHash = (h) => {
  if (!h) return "—";
  // "/static/js/main.2bfaa4ad.js" → "2bfaa4ad"
  const m = h.match(/main\.([a-z0-9]+)\.js/i);
  return m ? m[1] : h;
};

const _fmtAge = (iso) => {
  if (!iso) return "—";
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const h = Math.floor(ms / 3_600_000);
    if (h < 1)   return `${Math.floor(ms / 60_000)}m fa`;
    if (h < 24)  return `${h}h fa`;
    return `${Math.floor(h / 24)}g fa`;
  } catch { return "—"; }
};

const pct = (v) => `${Math.round((v || 0) * 100)}%`;

export const BuildAdoptionWidget = ({ top = 5 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true); setError(null);
    try {
      const token = localStorage.getItem("lojack_token") || "";
      const r = await axios.get(`${API}/admin/build-adoption/stats?top=${top}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [top]);

  if (loading) {
    return (
      <div className="bg-[#111] border border-gray-800 rounded-2xl p-5" data-testid="build-adoption-loading">
        <p className="text-gray-500 text-sm flex items-center gap-2">
          <RefreshCw size={14} className="animate-spin" /> Carico build adoption…
        </p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-[#111] border border-red-500/30 rounded-2xl p-5" data-testid="build-adoption-error">
        <p className="text-red-300 text-sm">{error || "Dati non disponibili"}</p>
      </div>
    );
  }

  const items = data.items || [];
  const latest = items[0];
  const latestBadge = latest ? _badgeForRate(latest.adoption_1h, latest.total_users) : null;
  const latestTheme = latestBadge ? THEMES[latestBadge.theme] : THEMES.neutral;
  const LatestIcon = latestTheme.Icon;

  return (
    <div className="bg-[#111] border border-gray-800 rounded-2xl p-5 space-y-4" data-testid="build-adoption-widget">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Package size={16} className="text-lojack-blue-400" />
            Build Adoption Rate
          </h3>
          <p className="text-gray-500 text-xs mt-0.5">
            Quanti utenti hanno caricato il nuovo bundle entro 1h dal deploy · validazione BuildVersionWatcher
          </p>
        </div>
        <button onClick={fetchData} className="text-gray-500 hover:text-gray-300 p-1" aria-label="Aggiorna" data-testid="build-adoption-refresh">
          <RefreshCw size={14} />
        </button>
      </div>

      {/* Latest deploy spotlight */}
      {latest ? (
        <div className={`${latestTheme.bg} rounded-xl p-4 border ${latestTheme.border} flex items-center gap-4`} data-testid="build-adoption-latest">
          <div className={`w-12 h-12 rounded-full ${latestTheme.bg} flex items-center justify-center border ${latestTheme.border}`}>
            <LatestIcon size={22} className={latestTheme.text} />
          </div>
          <div className="flex-1">
            <p className="text-gray-400 text-xs uppercase tracking-wider">Ultimo deploy · {_shortHash(latest.bundle_hash)} ({_fmtAge(latest.first_seen)})</p>
            <p className={`text-3xl font-bold ${latestTheme.text}`} data-testid="build-adoption-latest-rate">{pct(latest.adoption_1h)}</p>
            <p className="text-gray-500 text-xs mt-0.5">
              {latestBadge.label} · <b>{latest.within_1h_users}/{latest.total_users}</b> utenti loggati entro 1h
            </p>
          </div>
        </div>
      ) : (
        <p className="text-gray-500 text-sm">Nessun deploy tracciato finora. Le metriche appariranno dopo il prossimo redeploy.</p>
      )}

      {/* History table (compact) */}
      {items.length > 1 && (
        <div className="border border-gray-800 rounded-lg overflow-hidden" data-testid="build-adoption-history">
          <table className="w-full text-xs">
            <thead className="bg-black/40 text-gray-500 uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Bundle</th>
                <th className="text-left px-3 py-2 font-medium">Quando</th>
                <th className="text-right px-3 py-2 font-medium">Utenti</th>
                <th className="text-right px-3 py-2 font-medium">Eventi</th>
                <th className="text-right px-3 py-2 font-medium">≤1h</th>
                <th className="text-right px-3 py-2 font-medium">Adoption</th>
              </tr>
            </thead>
            <tbody>
              {items.slice(1).map((it) => {
                const b = _badgeForRate(it.adoption_1h, it.total_users);
                const t = THEMES[b.theme];
                return (
                  <tr key={it.bundle_hash} className="border-t border-gray-800/60 hover:bg-black/40">
                    <td className="px-3 py-2 font-mono text-gray-300">{_shortHash(it.bundle_hash)}</td>
                    <td className="px-3 py-2 text-gray-500">{_fmtAge(it.first_seen)}</td>
                    <td className="px-3 py-2 text-right text-gray-300">{it.total_users}</td>
                    <td className="px-3 py-2 text-right text-gray-500">{it.total_events}</td>
                    <td className="px-3 py-2 text-right text-gray-300">{it.within_1h_users}</td>
                    <td className={`px-3 py-2 text-right font-semibold ${t.text}`}>{pct(it.adoption_1h)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer hint */}
      <p className="text-gray-600 text-[11px] flex items-start gap-1.5 leading-relaxed">
        <Clock size={12} className="flex-shrink-0 mt-0.5" />
        Metriche basate solo su utenti loggati (admin/dealer/seller) — sessioni anonime e finestre incognito non vengono conteggiate.
        Retention: 30 giorni. Suggerimento: se l'adoption ≤1h resta sotto il 80% per 2 deploy consecutivi,
        considera di rendere il banner del watcher non-dismissibile.
      </p>
    </div>
  );
};

export default BuildAdoptionWidget;
